import { useEffect, useState } from 'react';
import {
  collection,
  onSnapshot,
  query,
  type Query,
  type QueryConstraint,
  type DocumentData,
  type FirestoreError,
} from 'firebase/firestore';
import { db } from '../firebase';

export interface CollectionResult<T> {
  data: (T & { id: string })[];
  loading: boolean;
  error: FirestoreError | null;
}

/** Subscribe to a Firestore collection path with optional constraints. */
export function useCollection<T = DocumentData>(
  path: string | null,
  ...constraints: QueryConstraint[]
): CollectionResult<T> {
  const [state, setState] = useState<CollectionResult<T>>({
    data: [],
    loading: !!path,
    error: null,
  });

  useEffect(() => {
    if (!path) {
      setState({ data: [], loading: false, error: null });
      return;
    }
    const ref = collection(db, path) as unknown as Query<T>;
    const q = constraints.length ? query(ref, ...constraints) : ref;
    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map((d) => ({ ...(d.data() as T), id: d.id }));
        setState({ data, loading: false, error: null });
      },
      (error) => setState({ data: [], loading: false, error }),
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, JSON.stringify(constraints)]);

  return state;
}
